/**
 * Helpers pour la gestion des Teams
 */

/**
 * Vérifie si l'utilisateur est owner de la team actuelle
 */
export function isTeamOwner(session) {
    return session?.user?.currentTeam?.role === 'owner';
}

/**
 * Vérifie si l'utilisateur est manager de la team actuelle
 */
export function isTeamManager(session) {
    return session?.user?.currentTeam?.role === 'manager';
}

/**
 * Vérifie si l'utilisateur est membre de la team actuelle
 */
export function isTeamMember(session) {
    return session?.user?.currentTeam?.role === 'member';
}

/**
 * Vérifie si l'utilisateur a des droits de gestion (owner ou manager)
 */
export function hasManagementRights(session) {
    const role = session?.user?.currentTeam?.role;
    return role === 'owner' || role === 'manager';
}

/**
 * Vérifie si l'utilisateur a accès à une team spécifique
 */
export function hasTeamAccess(session, teamId) {
    if (!session?.user?.availableTeams) return false;
    return session.user.availableTeams.some(team => team.id === teamId);
}

/**
 * Vérifie si l'utilisateur a une permission spécifique dans la team actuelle
 */
export function hasTeamPermission(session, permission) {
    if (!session?.user?.currentTeam?.permissions) return false;
    return session.user.currentTeam.permissions.includes(permission);
}

/**
 * Vérifie si l'utilisateur a une permission donnée
 * Alias pour hasTeamPermission pour compatibilité
 */
export function hasPermission(session, permission) {
    return hasTeamPermission(session, permission);
}

/**
 * Permissions granulaires pour V1
 */
export const TeamPermissions = {
    // Gestion des membres
    VIEW_MEMBERS: 'view_members',
    INVITE_MEMBERS: 'invite_members',
    SUSPEND_MEMBERS: 'suspend_members',
    MANAGE_MEMBERS: 'manage_members',

    // Analytics
    VIEW_ANALYTICS: 'view_analytics',
    EXPORT_ANALYTICS: 'export_analytics',

    // Formations
    ASSIGN_COURSES: 'assign_courses',
    VIEW_ASSIGNMENTS: 'view_assignments',

    // Paramètres
    MANAGE_TEAM_SETTINGS: 'manage_team_settings',
    MANAGE_BILLING: 'manage_billing',

    // Rapports
    VIEW_REPORTS: 'view_reports',
    GENERATE_REPORTS: 'generate_reports',

    // Leaderboard
    VIEW_LEADERBOARD: 'view_leaderboard',
    MANAGE_LEADERBOARD: 'manage_leaderboard'
};

/**
 * Obtient les permissions par défaut selon le rôle
 */
export function getDefaultPermissions(role) {
    if (role === 'owner') {
        // Owner a toutes les permissions
        return [
            TeamPermissions.VIEW_MEMBERS,
            TeamPermissions.INVITE_MEMBERS,
            TeamPermissions.SUSPEND_MEMBERS,
            TeamPermissions.MANAGE_MEMBERS,
            TeamPermissions.VIEW_ANALYTICS,
            TeamPermissions.EXPORT_ANALYTICS,
            TeamPermissions.ASSIGN_COURSES,
            TeamPermissions.VIEW_ASSIGNMENTS,
            TeamPermissions.MANAGE_TEAM_SETTINGS,
            TeamPermissions.MANAGE_BILLING,
            TeamPermissions.VIEW_REPORTS,
            TeamPermissions.GENERATE_REPORTS,
            TeamPermissions.VIEW_LEADERBOARD,
            TeamPermissions.MANAGE_LEADERBOARD
        ];
    } else if (role === 'manager') {
        // Manager a presque toutes les permissions
        return [
            TeamPermissions.VIEW_MEMBERS,
            TeamPermissions.INVITE_MEMBERS,
            TeamPermissions.SUSPEND_MEMBERS,
            TeamPermissions.MANAGE_MEMBERS,
            TeamPermissions.VIEW_ANALYTICS,
            TeamPermissions.EXPORT_ANALYTICS,
            TeamPermissions.ASSIGN_COURSES,
            TeamPermissions.VIEW_ASSIGNMENTS,
            TeamPermissions.MANAGE_TEAM_SETTINGS,
            TeamPermissions.MANAGE_BILLING,
            TeamPermissions.VIEW_REPORTS,
            TeamPermissions.GENERATE_REPORTS,
            TeamPermissions.VIEW_LEADERBOARD,
            TeamPermissions.MANAGE_LEADERBOARD
        ];
    } else if (role === 'member') {
        // Member a des permissions limitées
        return [
            TeamPermissions.VIEW_MEMBERS,
            TeamPermissions.VIEW_ASSIGNMENTS,
            TeamPermissions.VIEW_LEADERBOARD
        ];
    }
    return [];
}

/**
 * Formatte le nombre de sièges disponibles
 */
export function formatSeatsAvailable(total, occupied) {
    const available = total - occupied;
    if (available === 0) {
        return "Aucun siège disponible";
    } else if (available === 1) {
        return "1 siège disponible";
    } else {
        return `${available} sièges disponibles`;
    }
}

/**
 * Calcule le taux d'utilisation des sièges
 */
export function calculateSeatsUtilization(total, occupied) {
    if (total === 0) return 0;
    return Math.round((occupied / total) * 100);
}

/**
 * Détermine la couleur selon le niveau de risque d'inactivité
 */
export function getInactivityRiskLevel(daysInactive) {
    if (daysInactive >= 14) return { level: 'critical', color: 'red' };
    if (daysInactive >= 7) return { level: 'high', color: 'orange' };
    if (daysInactive >= 3) return { level: 'medium', color: 'yellow' };
    return { level: 'low', color: 'green' };
}

/**
 * Formatte la date de dernière activité
 */
export function formatLastActivity(date) {
    if (!date) return "Jamais";

    const now = new Date();
    const activityDate = new Date(date);
    const diffTime = Math.abs(now - activityDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffMinutes < 60) {
        return `Il y a ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
    } else if (diffHours < 24) {
        return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
    } else if (diffDays === 1) {
        return "Hier";
    } else if (diffDays < 7) {
        return `Il y a ${diffDays} jours`;
    } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `Il y a ${weeks} semaine${weeks > 1 ? 's' : ''}`;
    } else {
        const months = Math.floor(diffDays / 30);
        return `Il y a ${months} mois`;
    }
}

/**
 * Calcule les statistiques de progression de l'équipe
 */
export function calculateTeamProgress(members) {
    if (!members || members.length === 0) {
        return {
            avgCompletion: 0,
            totalXP: 0,
            totalBadges: 0,
            activeCount: 0,
            inactiveCount: 0
        };
    }

    const stats = members.reduce((acc, member) => {
        acc.totalXP += member.xp || 0;
        acc.totalBadges += member.badges || 0;
        acc.totalCompletion += member.completionRate || 0;

        if (member.lastActivity && new Date(member.lastActivity) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)) {
            acc.activeCount++;
        } else {
            acc.inactiveCount++;
        }

        return acc;
    }, { totalXP: 0, totalBadges: 0, totalCompletion: 0, activeCount: 0, inactiveCount: 0 });

    return {
        avgCompletion: Math.round(stats.totalCompletion / members.length),
        totalXP: stats.totalXP,
        totalBadges: stats.totalBadges,
        activeCount: stats.activeCount,
        inactiveCount: stats.inactiveCount
    };
}